import { useState, useEffect, useCallback } from 'react';
import { fmtMoney } from '@/lib/format';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { SectionHeader, PageSkeleton, LoadError } from '@/components/dashboard/MetricCard';
import { MembershipStatusCard } from '@/components/dashboard/MembershipStatusCard';
import { ReferralLinksCard } from '@/components/dashboard/ReferralLinksCard';
import { ChangePasswordCard } from '@/components/dashboard/ChangePasswordCard';
import { Check, User, Mail, Shield, Calendar, Award, Users, TrendingUp, Eye, EyeOff, Loader2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAccess, TIER_LABEL } from '@/components/dashboard/access';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';

const T = {
  es: {
    privacyHidden: 'Tu red solo verá tu nombre',
    privacyShown: 'Tu red podrá ver tu email y tu teléfono',
    saveError: 'No se ha podido guardar',
    name: 'Nombre',
    email: 'Email',
    membership: 'Membresía',
    brokerLevel: 'Nivel de broker',
    memberSince: 'Miembro desde',
    directAffiliates: 'Afiliados directos',
    network: 'Red (3 niveles)',
    commissions: 'Comisiones pagadas',
    title: 'Mi perfil',
    subtitle: 'Tu información personal y tu enlace de invitación',
    emailVerified: 'Email verificado',
    privacyTitle: 'Tus datos dentro de tu red',
    privacyText: 'Si te registraste con el enlace de otro miembro, los brokers Premium que están por encima de ti (hasta tres niveles) pueden ver tu email y tu teléfono, solo para ayudarte dentro del club (cláusula 14.6 de los T&C).',
    seesNameOnly: 'Tu red solo ve tu nombre',
    seesAll: 'Tu red ve tu nombre, email y teléfono',
    showContact: 'Mostrar mi email y teléfono',
    hideContact: 'Ocultar mi email y teléfono',
    langText: 'El idioma del panel y de los emails que te enviamos.',
  },
  en: {
    privacyHidden: 'Your network will only see your name',
    privacyShown: 'Your network will be able to see your email and phone number',
    saveError: 'We couldn’t save this',
    name: 'Name',
    email: 'Email',
    membership: 'Membership',
    brokerLevel: 'Broker level',
    memberSince: 'Member since',
    directAffiliates: 'Direct affiliates',
    network: 'Network (3 levels)',
    commissions: 'Commissions paid',
    title: 'My profile',
    subtitle: 'Your personal details and your invitation link',
    emailVerified: 'Email verified',
    privacyTitle: 'Your details within your network',
    privacyText: 'If you signed up through another member’s link, the Premium brokers above you (up to three levels) can see your email and phone number, solely to help you within the club (clause 14.6 of the T&Cs).',
    seesNameOnly: 'Your network only sees your name',
    seesAll: 'Your network sees your name, email and phone number',
    showContact: 'Show my email and phone number',
    hideContact: 'Hide my email and phone number',
    langText: 'The language of your dashboard and of the emails we send you.',
  },
};

const LANGS = [['es', 'Español'], ['en', 'English']];

export default function DashboardPerfil() {
  const { user } = useDashboard();
  const { tier, isBroker, isMember } = useAccess();
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { lang, setLang } = useSiteLang();
  const t = T[lang] || T.es;

  // Igual que las banderas de la cabecera (DashLangSwitch): cambia al momento y lo guarda en la cuenta
  const pickLang = (code) => {
    if (code === lang) return;
    setLang(code);
    try {
      const stored = JSON.parse(sessionStorage.getItem('dashboard_user') || 'null');
      if (stored) sessionStorage.setItem('dashboard_user', JSON.stringify({ ...stored, lang: code }));
    } catch { /* sin almacenamiento */ }
    dashboardApi.setMyLang(code).catch(() => {});
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    dashboardApi.profile()
      .then(setProfile)
      .catch((e) => { console.error(e); setError(e.message || 'Error'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // T&C 14.6.4: el miembro decide si su email y su teléfono se muestran a su red
  const contactHidden = !!(profile || user)?.network_contact_hidden;
  const togglePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const r = await dashboardApi.updateContactPrivacy(!contactHidden);
      setProfile((prev) => ({ ...(prev || user), network_contact_hidden: r.network_contact_hidden }));
      toast.success(r.network_contact_hidden ? t.privacyHidden : t.privacyShown);
    } catch (e) {
      toast.error(e.message || t.saveError);
    } finally {
      setSavingPrivacy(false);
    }
  };

  if (loading) return <PageSkeleton cards={0} rows={7} />;

  const p = profile || user;
  const refCode = p?.referral_code || p?.referralCode;
  // join_date llega como 2026-03-15 (o ISO con hora): se muestra dd/mm/aaaa
  const joinMatch = String(p?.join_date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const joinDate = joinMatch ? `${joinMatch[3]}/${joinMatch[2]}/${joinMatch[1]}` : (p?.join_date || '—');

  const fields = [
    { icon: User, label: t.name, value: p?.name },
    { icon: Mail, label: t.email, value: p?.email },
    { icon: Shield, label: t.membership, value: TIER_LABEL[tier] || 'Free' },
    isBroker && p?.level && { icon: Award, label: t.brokerLevel, value: p?.level },
    { icon: Calendar, label: t.memberSince, value: joinDate },
    isBroker && { icon: Users, label: t.directAffiliates, value: profile?.affiliates_count || 0 },
    isBroker && { icon: Users, label: t.network, value: profile?.network_count || 0 },
    isMember && { icon: TrendingUp, label: t.commissions, value: `${fmtMoney(profile?.total_commissions || 0, 'eur')}` },
  ].filter(Boolean);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="dashboard-perfil">
      <SectionHeader title={t.title} subtitle={t.subtitle} />

      <MembershipStatusCard />

      {error ? <LoadError onRetry={load} /> : (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-1"
      >
        <div className="flex items-center gap-4 mb-6 pb-5 border-b border-white/[0.04]">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#a08339] flex items-center justify-center text-[#0a1628] text-2xl font-bold shadow-xl shadow-[#c9a84c]/10">
              {p?.name?.charAt(0)}
            </div>
            {p?.email_verified !== false && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#060d1b] flex items-center justify-center" title={t.emailVerified}>
                <Check size={10} className="text-white" />
              </div>
            )}
          </div>
          <div>
            <h3 className="text-white text-lg font-semibold">{p?.name}</h3>
            <p className="text-[#c9a84c] text-xs uppercase tracking-wider font-medium">{TIER_LABEL[tier] || 'Free'}</p>
          </div>
        </div>
        {fields.map((f, i) => (
          <motion.div
            key={f.label}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25, delay: Math.min(i, 6) * 0.03 }}
            className="flex items-center gap-3 py-3 border-b border-white/[0.03] last:border-0"
          >
            <div className="w-7 h-7 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
              <f.icon size={13} className="text-gray-400" />
            </div>
            <span className="text-sm text-gray-400 w-28 sm:w-40 shrink-0">{f.label}</span>
            <span className="text-sm text-white min-w-0 break-all">{f.value}</span>
          </motion.div>
        ))}
      </motion.div>
      )}

      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid="profile-privacy">
        <h3 className="text-white text-sm font-semibold">{t.privacyTitle}</h3>
        <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">
          {t.privacyText}
        </p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <p className="text-sm text-gray-300 flex items-center gap-2">
            {contactHidden ? <EyeOff size={15} className="text-gray-400" /> : <Eye size={15} className="text-[#c9a84c]" />}
            {contactHidden ? t.seesNameOnly : t.seesAll}
          </p>
          <button type="button" onClick={togglePrivacy} disabled={savingPrivacy}
            className="inline-flex items-center justify-center gap-2 min-h-[40px] px-4 rounded-lg border border-white/10 text-sm text-gray-200 hover:border-[#c9a84c]/40 hover:text-white disabled:opacity-60"
            data-testid="profile-privacy-toggle">
            {savingPrivacy && <Loader2 size={14} className="animate-spin" />}
            {contactHidden ? t.showContact : t.hideContact}
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid="profile-language">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h3 className="text-white text-sm font-semibold flex items-center gap-2"><Globe size={15} className="text-[#c9a84c]" /> Idioma / Language</h3>
            <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{t.langText}</p>
          </div>
          <div className="inline-flex rounded-lg border border-white/10 p-1 self-start sm:self-auto" role="group" aria-label="Idioma / Language">
            {LANGS.map(([code, name]) => (
              <button key={code} type="button" onClick={() => pickLang(code)} aria-pressed={lang === code}
                className={`min-h-[36px] px-4 rounded-md text-sm transition-colors ${lang === code ? 'bg-[#c9a84c] text-[#0a1628] font-semibold' : 'text-gray-300 hover:text-white'}`}
                data-testid={`profile-lang-${code}`}>
                {name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <ChangePasswordCard />

      <ReferralLinksCard refCode={refCode} variant="full" delay={0.2} />
    </div>
  );
}
